/**
 * AI Recruit - Assignment API Service
 * API functions for assignment tracking and audit trail
 */

import { apiV2 } from './api'

// ============================================
// TYPE DEFINITIONS
// ============================================

export interface AssignmentUpdateRequest {
  assigned_to_user_id: string
  change_reason?: string
}

export interface AssignmentUpdateResponse {
  success: boolean
  assignment: {
    assigned_to_user_id: string
    assigned_to_user_name: string
    updated_at: string
  }
}

export interface AuditHistoryItem {
  id: string
  change_type: 'assignment_change' | 'status_change' | 'resume_download'
  changed_at: string
  changed_by_user_name: string
  // Assignment change fields
  old_assigned_to_user_name?: string | null
  new_assigned_to_user_name?: string | null
  change_reason?: string | null
  // Status change fields
  old_status?: string | null
  new_status?: string | null
}

export interface AuditHistoryResponse {
  audit_history: AuditHistoryItem[]
  total_count: number
  page: number
  total_pages: number
  has_next: boolean
  has_previous: boolean
}

export interface OrganizationUser {
  id: string
  full_name: string
  email: string
  role: string
}

export interface OrganizationUsersResponse {
  users: OrganizationUser[]
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Update resume assignment
 */
export const updateResumeAssignment = async (
  resumeId: string,
  userId: string,
  reason?: string
): Promise<AssignmentUpdateResponse> => {
  const response = await apiV2.patch<AssignmentUpdateResponse>(
    `/resumes/${resumeId}/assignment`,
    {
      assigned_to_user_id: userId,
      change_reason: reason,
    }
  )
  return response.data
}

/**
 * Update job match assignment
 */
export const updateJobMatchAssignment = async (
  jobMatchId: string,
  userId: string,
  reason?: string
): Promise<AssignmentUpdateResponse> => {
  const response = await apiV2.patch<AssignmentUpdateResponse>(
    `/job-matches/${jobMatchId}/assignment`,
    {
      assigned_to_user_id: userId,
      change_reason: reason,
    }
  )
  return response.data
}

/**
 * Get audit history for an assignment
 */
export const getAuditHistory = async (
  type: 'resume_upload' | 'job_match',
  entityId: string,
  page: number = 1,
  limit: number = 5
): Promise<AuditHistoryResponse> => {
  const response = await apiV2.get<AuditHistoryResponse>(
    '/assignments/audit-history',
    {
      params: {
        type,
        entity_id: entityId,
        page,
        limit,
      },
    }
  )
  return response.data
}

/**
 * Get organization users for assignment dropdown
 */
export const getOrganizationUsers = async (
  search?: string,
  limit: number = 50
): Promise<OrganizationUsersResponse> => {
  const response = await apiV2.get<OrganizationUsersResponse>(
    '/users/organization',
    {
      params: {
        search,
        limit,
      },
    }
  )
  return response.data
}
